import { FIELD_TYPES } from 'src/tpbp-core/models/fields/field-set.const'
import { TEXT_INPUT_FORMATS } from 'src/tpbp-core/models/fields/text-input.const'
import { OBJECT_TYPES } from 'src/tpbp-configuration/models/models.const'
import { standardDateControls } from 'src/services/mappings/mapping.utils'
import type {
  DynamicEnumsRuleInterface,
  FieldInterface,
  RelationInterface,
} from 'src/tpbp-configuration/mapper/mappingInterfaces'
import {
  getCspEnums,
  getCspLabel,
  getCspCategoryEnums,
  getCspCategoryLabel,
} from 'src/services/mappings/profession/profession.mapping.utils'
import { logger } from 'src/utils/logger.utils'
import { FIELD_RULES } from 'src/tpbp-core/models/fields/fieldRules.const'

const cspCategoryEnumRules: DynamicEnumsRuleInterface[] = [
  {
    expression: () => true,
    getEnumKeys: () => getCspCategoryEnums(),
  },
]
const cspCategoryDynamicEnumLabel = (record) =>
  getCspCategoryLabel(record.category)

const cspEnumRules: DynamicEnumsRuleInterface[] = [
  {
    expression: () => true,
    getEnumKeys: (record) => getCspEnums((record?.category ?? '') as string),
  },
]
const cspDynamicEnumLabel = (record) => getCspLabel(record.csp)

const professionFields: FieldInterface[] = [
  {
    key: 'name',
    originKey: 'name',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'status',
    originKey: 'status',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'category',
    originKey: 'category',
    type: FIELD_TYPES.number,
    rule: FIELD_RULES.isMandatory,
    enumRules: cspCategoryEnumRules,
    dynamicEnumLabel: cspCategoryDynamicEnumLabel,
  },
  {
    key: 'categoryLabel',
    originKey: 'category_label',
    type: FIELD_TYPES.string,
  },
  {
    key: 'cspPartnerLabel',
    originKey: 'csp_partner_label',
    type: FIELD_TYPES.string,
  },
  {
    key: 'sector',
    originKey: 'sector',
    type: FIELD_TYPES.string,
  },
  {
    key: 'csp',
    originKey: 'csp',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isMandatory,
    enumRules: cspEnumRules,
    dynamicEnumLabel: cspDynamicEnumLabel,
  },
  {
    key: 'seniority',
    originKey: 'seniority',
    type: FIELD_TYPES.integer,
    rule: FIELD_RULES.isOptional,
    controls: standardDateControls,
  },
  {
    key: 'startDate',
    originKey: 'start_date',
    type: FIELD_TYPES.date,
    rule: FIELD_RULES.isMandatory,
    controls: {
      min: (record, { D_150Y }) => {
        if (!record._person) {
          logger.warn('missing _person in profession object')
        }
        return record._person?.dateOfBirth || D_150Y
      },
      max: (_record, { TODAY }) => TODAY,
    },
  },
  {
    key: 'endDate',
    originKey: 'end_date',
    type: FIELD_TYPES.date,
    rule: FIELD_RULES.isOptional,
    controls: standardDateControls,
  },
  {
    key: 'deathBenefitByPerson',
    originKey: 'death_benefit_by_person',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'deathAnnuityForPartner',
    originKey: 'death_annuity_for_partner',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'educationAnnuityUntil18',
    originKey: 'education_annuity_until_18',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'educationAnnuityUntil25',
    originKey: 'education_annuity_until_25',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'higherEducationAmount',
    originKey: 'higher_education_amount',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'minorChildStudiesAmount',
    originKey: 'minor_child_studies_amount',
    type: FIELD_TYPES.number,
    format: TEXT_INPUT_FORMATS.currency,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'collectiveConvention',
    originKey: 'collective_convention',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'college',
    originKey: 'college',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
  {
    key: 'cspLabel',
    originKey: 'csp_label',
    type: FIELD_TYPES.string,
    rule: FIELD_RULES.isOptional,
  },
]

const professionRelations: RelationInterface[] = [
  {
    key: '_budgetItems',
    originKey: 'budget_items',
    objectType: OBJECT_TYPES.budgetItem,
    isMultiple: true,
  },
  {
    key: '_person',
    originKey: 'person',
    objectType: OBJECT_TYPES.person,
    isMultiple: false,
    isPseudoAttribute: true,
  },
]

export { professionFields, professionRelations }
